import type { PropsWithChildren } from 'react'
import { useEffect, useState, useMemo } from 'react'
import { useLocation, Outlet } from 'react-router-dom'

import { DynamicPopups } from 'src/app/popups/components/dynamic-popups'
import AppWindowFrame from 'src/app/router/layouts/navigation/app-window-frame'
import NavMobile from 'src/app/router/layouts/navigation/nav-mobile'
import { GoogleMapsProvider } from 'src/features/google-maps/google-maps-context'
import { SidebarProvider } from 'src/shared/components/ui/miscellaneous/sidebar'
import { DelayedSuspense } from 'src/shared/components/utils/delayed-suspense'
import { UserSearchResults } from 'src/shared/features/users/components/list/user-search-results'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { cn } from 'src/shared/utils/cn'

const Fallback = () => {
  return (
    <div className="flex w-full flex-col divide-y">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="flex animate-pulse items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <div className="size-12 rounded-full bg-muted" />
            <div className="flex flex-col items-start gap-2">
              <div className="h-4 w-32 rounded bg-muted" />
              <div className="h-3 w-24 rounded bg-muted" />
            </div>
          </div>
          <div className="size-5 rounded bg-muted" />
        </div>
      ))}
    </div>
  )
}

interface AuthenticatedLayoutProps extends PropsWithChildren {
  hideHeader?: boolean
  hideFooter?: boolean
}

const AuthenticatedLayout = ({ children, hideHeader: _hideHeader, hideFooter }: AuthenticatedLayoutProps) => {
  // const isMobile = useIsMobile()
  const { isMobile } = useBreakpoint()
  const location = useLocation()
  // const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  // const { data: tutorials } = useUserTutorials()

  // Override hideHeader to always show the header
  const shouldShowHeader = true

  // TODO: Uncomment once the web is ready to show the recap page.
  // Check if weeklyRecapSeen is false and navigate to recap page
  // useEffect(() => {
  //   if (tutorials && tutorials.weeklyRecapSeen === false && !location.pathname.includes(paths.recap.path)) {
  //     // Create a date with ISO format and then manually format it to the required pattern
  //     const now = new Date()
  //     const year = now.getUTCFullYear()
  //     const month = String(now.getUTCMonth() + 1).padStart(2, '0')
  //     const day = String(now.getUTCDate()).padStart(2, '0')
  //     const hours = String(now.getUTCHours()).padStart(2, '0')
  //     const minutes = String(now.getUTCMinutes()).padStart(2, '0')
  //     const seconds = String(now.getUTCSeconds()).padStart(2, '0')
  //     const milliseconds = String(now.getUTCMilliseconds()).padStart(3, '0')

  //     // Add microseconds (always 000 since JS only has millisecond precision)
  //     const microseconds = milliseconds + '000'

  //     // Format as: yyyy-MM-dd%20HH:mm:ss.microseconds+00:00
  //     const today = `${year}-${month}-${day}%20${hours}:${minutes}:${seconds}.${microseconds}+00:00`

  //     navigate(paths.recap.getHref(today))
  //   }
  // }, [tutorials, location.pathname, navigate])

  // Smoother scroll restoration
  useEffect(() => {
    // Only scroll to top for new paths, not search param changes
    if (location.pathname !== window.history.state?.prevPathname) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
    // Store current pathname for future comparison
    window.history.replaceState({ ...window.history.state, prevPathname: location.pathname }, '')
  }, [location.pathname])

  const handleSearch = (query: string) => {
    setSearchQuery(query)
  }

  // Wrap Chat and ChatProvider in a memo to prevent unnecessary re-renders
  const LayoutWrapper = useMemo(() => {
    const content = searchQuery ? (
      <div className="flex w-full flex-col gap-8">
        <DelayedSuspense fallback={<Fallback />}>
          <UserSearchResults query={searchQuery} />
        </DelayedSuspense>
      </div>
    ) : (
      children || <Outlet />
    )

    return (
      <GoogleMapsProvider>
        <SidebarProvider>
          {isMobile ? (
            <>
              <NavMobile hideHeader={!shouldShowHeader} hideFooter={hideFooter} onSearch={handleSearch} />
              <main className="flex min-h-screen w-full flex-col">
                {shouldShowHeader && <div className="h-14 w-full" />}
                <div className={cn('w-full flex-1', !hideFooter && 'mb-12')}>{content}</div>
                {!hideFooter && <div className="h-16 w-full" />}
              </main>
            </>
          ) : (
            <AppWindowFrame>{content}</AppWindowFrame>
          )}
          <DynamicPopups />
        </SidebarProvider>
      </GoogleMapsProvider>
    )
  }, [isMobile, shouldShowHeader, hideFooter, searchQuery, children])

  return LayoutWrapper
}

export default AuthenticatedLayout
